import {
    AvatarType,
    ExAvatar,
    ExIcon,
    ExTooltip,
    IconSize,
    TooltipAlignment,
} from '@boomi/exosphere';
import {
    type ApprovalComparisonType,
    type ApprovalConfiguration,
    type ApprovalConfigurationDetails,
    ApprovalConfigurationStatus,
    type ApprovalEvent,
    type DetailTask,
} from '../../sources';
import './task-timeline.css';
import classNames from 'classnames';
import DateWithTooltip from '../../DateWithTooltip';
import TaskStatusBadge from '../../TaskStatusBadge';

interface Props {
    selectedTask: DetailTask;
}

const getEvent = (approvalEvent: ApprovalEvent, badgeText: string = '') => {
    const fullName = approvalEvent.createdBy.split('@')[0] || approvalEvent.createdBy;
    const firstName = approvalEvent.createdBy.split(/\b/)[0] || fullName;
    const lastName = approvalEvent.createdBy.split(/\b/)[2] || '';

    return (
        <div
            className="timeline-row"
            key={approvalEvent.id}
            data-testid={`timeline-row-${approvalEvent.id}`}
        >
            <span className="timeline-item">
                <div className="timeline-header">
                    <ExAvatar
                        className="timeline-avatar"
                        avatarType={AvatarType.USER}
                        firstName={firstName}
                        lastName={lastName}
                    />
                    <span className="timeline-created-by">
                        <ExTooltip alignment={TooltipAlignment.MIDDLE}>
                            {approvalEvent.createdBy}
                            <span slot="anchor">
                                {`${firstName}${lastName ? ` ${lastName}` : ''}`}{' '}
                            </span>
                        </ExTooltip>
                        <TaskStatusBadge
                            status={badgeText !== '' ? badgeText : approvalEvent.status}
                        />
                    </span>
                    <DateWithTooltip
                        dateString={approvalEvent.createdAt}
                        alignment={TooltipAlignment.END}
                    />
                </div>
                {approvalEvent.comment ? (
                    <div className="timeline-comment">
                        <ExIcon icon="Chatcircletext" size={IconSize.XS} />
                        <span className="timeline-comment-text">{approvalEvent.comment}</span>
                    </div>
                ) : null}
            </span>
        </div>
    );
};

const getTaskTimelineConfigDetails = (
    approvalConfig: ApprovalConfiguration,
    approvalEvents: ApprovalEvent[],
) =>
    approvalConfig.details.map((configDetails, index) => {
        let totalApprovals = 0;
        let totalDenials = 0;

        const eventsForConfigDetails = approvalEvents.filter(
            (event) => event.approvalConfigurationDetailId === configDetails.id,
        );

        eventsForConfigDetails.forEach((approvalEvent) => {
            if (approvalEvent.status === ApprovalConfigurationStatus.Approved) {
                totalApprovals++;
            }

            if (approvalEvent.status === ApprovalConfigurationStatus.Denied) {
                totalDenials++;
            }
        });

        const isGroupApproved = totalApprovals >= configDetails.minimumApprovals;
        const isGroupDenied = totalDenials >= configDetails.minimumDenials;

        const approvalDetailClasses = classNames('approval-detail-wrapper', {
            'approval-detail-complete': isGroupApproved,
            'approval-detail-denied': isGroupDenied && !isGroupApproved,
        });

        return (
            <div key={configDetails.id} className={approvalDetailClasses}>
                <div className="approval-detail-header">Group {index + 1}</div>
                <div className="approval-detail-content">
                    <span className="approval-detail-approval">
                        <ExIcon icon="Thumbsup" size={IconSize.S} hide-browser-tooltip="" />
                        {totalApprovals} (Min: {configDetails.minimumApprovals})
                    </span>
                    <span className="approval-detail-approval">
                        <ExIcon icon="Thumbsdown" size={IconSize.S} hide-browser-tooltip="" />
                        {totalDenials} (Max: {configDetails.minimumDenials})
                    </span>
                </div>
                {eventsForConfigDetails.map((approvalEvent) => getEvent(approvalEvent))}
            </div>
        );
    });

const TaskTimeline = ({ selectedTask }: Props) => {
    const getStageStatus = (
        details: ApprovalConfigurationDetails[],
        comparisonType: ApprovalComparisonType,
        configId: string,
        status: ApprovalConfigurationStatus,
    ) => {
        if (
            (comparisonType.toLowerCase() === 'any' &&
                details
                    .filter((detail) => detail.approvalConfigurationId === configId)
                    .some((ad) => ad.status.toLowerCase() === status.toLowerCase())) ||
            (comparisonType.toLowerCase() === 'all' &&
                details
                    .filter((detail) => detail.approvalConfigurationId === configId)
                    .every((ad) => ad.status.toLowerCase() === status.toLowerCase()))
        ) {
            return true;
        }

        return false;
    };

    const getApprovalConfigs = (approvalConfigs: ApprovalConfiguration[]) => {
        // Track the current lastest approved index so we can figure out which ones after the pending stage are 'upcoming'.
        let latestApprovedIndex = -1;

        return approvalConfigs
            .sort((a, b) => a.order - b.order)
            .map((approvalConfig, index) => {
                const isStageApproved = getStageStatus(
                    approvalConfig.details,
                    approvalConfig.approvalComparisonType,
                    approvalConfig.id,
                    ApprovalConfigurationStatus.Approved,
                );

                const isStageDenied = getStageStatus(
                    approvalConfig.details,
                    approvalConfig.approvalComparisonType,
                    approvalConfig.id,
                    ApprovalConfigurationStatus.Denied,
                );

                if (isStageApproved) {
                    latestApprovedIndex = index;
                }

                const timelineDotClasses = classNames('timeline-dot', {
                    'timeline-dot-latest': index === latestApprovedIndex + 1,
                    'timeline-dot-upcoming': index >= latestApprovedIndex + 2,
                });

                return (
                    <div key={approvalConfig.id} className="approval-config-wrapper">
                        {approvalConfigs.length > 1 &&
                            (isStageApproved ? (
                                <span className={timelineDotClasses}>
                                    <ExIcon
                                        className="timeline-dot-icon"
                                        icon="Success"
                                        hide-browser-tooltip=""
                                    />
                                </span>
                            ) : isStageDenied ? (
                                <span className={timelineDotClasses}>
                                    <ExIcon
                                        className="timeline-dot-icon"
                                        icon="XCircle"
                                        hide-browser-tooltip=""
                                    />
                                </span>
                            ) : (
                                <span className={timelineDotClasses}>
                                    <ExIcon
                                        className="timeline-dot-icon"
                                        icon="In progress"
                                        hide-browser-tooltip=""
                                    />
                                </span>
                            ))}
                        <div
                            className={`approval-config-content ${index >= latestApprovedIndex + 2 && 'approval-config-content-upcoming'}`}
                        >
                            <div className="approval-config-info">
                                <strong>Approval Stage {index + 1}</strong>
                                <span>
                                    {approvalConfig.approvalComparisonType === 'all'
                                        ? 'All group approvals required'
                                        : approvalConfig.approvalComparisonType === 'any'
                                          ? 'Any group approval required'
                                          : ''}
                                </span>
                            </div>
                            {getTaskTimelineConfigDetails(
                                approvalConfig,
                                selectedTask.approvalEvents,
                            )}
                        </div>
                    </div>
                );
            });
    };

    return (
        <div className="timeline-wrapper">
            {getEvent(
                {
                    id: 'created',
                    status: ApprovalConfigurationStatus.Approved,
                    comment: null,
                    createdBy: selectedTask.createdBy,
                    createdAt: selectedTask.createdAt,
                    updatedAt: selectedTask.updatedAt,
                },
                'Created',
            )}
            {getApprovalConfigs(selectedTask.approvalConfigurations)}
        </div>
    );
};

export default TaskTimeline;
